import React, { useState, useMemo, useEffect } from 'react';
import { Center, Stack } from '@fiverr-private/layout_components';
import type { Banner } from '@fiverr-private/store_builder_types';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { NOT_SURE_NICHE } from '@fiverr-private/store_builder_ui';
import { AppI18n } from '../../i18n/i18n';
import { useInitialProps } from '../../hooks/useInitialProps';
import { useStoreData } from '../../hooks/useStoreData';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useStepValidation } from '../../hooks/use-step-validation/useStepValidation';
import { calcChosenNicheId } from '../../utils/calculateChosenNicheId';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { BannerGrid } from '../../components/banner-grid/BannerGrid';
import { StepTitle } from '../../components/step-title/StepTitle';

const MAX_SELECTED_BANNERS = 2;

export const ChooseBanners: React.FC = () => {
    const { bannersData = [], nicheData = [] } = useInitialProps();
    const { storeData } = useStoreData();
    const { autoSave } = useAutoSave();
    const { currentPath } = useWizardNavigation();
    const { registerStepValidation, unregisterStepValidation } = useStepValidation();
    const [selectedBanners, setSelectedBanners] = useState<Banner[]>(storeData?.banners || []);
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    const chosenNicheId = storeData ? calcChosenNicheId({ storeData }) : null;
    const [isNotSure] = useState<boolean>(storeData?.niche.displayName === NOT_SURE_NICHE.displayName);
    const description = useMemo(() => {
        if (isNotSure) {
            return <AppI18n k="steps.choose_banners.not_sure_description" />;
        }
        return <AppI18n k="steps.choose_banners.description" />;
    }, [isNotSure]);

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    useEffect(() => {
        if (chosenNicheId && selectedBanners.length > 0) {
            const shouldResetSelection = selectedBanners.some((banner) => banner.nicheId !== chosenNicheId);
            if (shouldResetSelection) {
                setSelectedBanners([]);
            }
        }
    }, [chosenNicheId, selectedBanners]);

    useEffect(() => {
        const validationStatusFn = () => (selectedBanners.length === MAX_SELECTED_BANNERS ? 'valid' : 'invalid');

        registerStepValidation('choose-banners', validationStatusFn);

        return () => {
            unregisterStepValidation('choose-banners');
        };
    }, [selectedBanners, registerStepValidation, unregisterStepValidation]);

    const handleBannerClick = (bannerId: string) => {
        setSelectedBanners((prevSelected) => {
            const isAlreadySelected = prevSelected.some((banner) => banner.id === bannerId);

            if (isAlreadySelected) {
                const newSelection = prevSelected.filter((banner) => banner.id !== bannerId);
                autoSave({ banners: newSelection, niche: nicheData.find((n) => n.id === chosenNicheId) });
                return newSelection;
            }

            if (prevSelected.length >= MAX_SELECTED_BANNERS) {
                return prevSelected;
            }

            const bannerToAdd = bannersData.find((banner) => banner.id === bannerId);
            if (!bannerToAdd) {
                return prevSelected;
            }

            const newSelection = [...prevSelected, bannerToAdd];
            autoSave({ banners: newSelection, niche: nicheData.find((n) => n.id === chosenNicheId) });
            return newSelection;
        });
    };

    const bannersToDisplay = useMemo(
        () => bannersData.filter((banner) => banner.nicheId === chosenNicheId),
        [bannersData, chosenNicheId]
    );

    return (
        <Center>
            <Stack ref={pageImpressionRef} direction="column" justifyContent="flexStart" maxWidth={552} gap="8">
                <StepTitle title={<AppI18n k="steps.choose_banners.title" />} description={description} />
                <BannerGrid
                    bannersToDisplay={bannersToDisplay}
                    selectedBanners={selectedBanners}
                    handleBannerClick={handleBannerClick}
                />
            </Stack>
        </Center>
    );
};

ChooseBanners.displayName = 'ChooseBanners';
